import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { ChatComponentType, Message } from "@/types";
import { MeetingContext } from "@/pages/Meeting";
import { storage } from "@/util";
import { LOGININFOSTORE, LOGINTOKEN } from "@/config/const";
import { WebImUi } from "@/web-im-ui-sdk/index.js";
import "@/web-im-ui-sdk/style.css";

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
`;

const HeaderBar = styled.div`
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 0 10px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
`;

const HeaderAlert = styled.span`
  color: #dc2626;
  font-size: 12px;
  white-space: nowrap;
`;

const HeaderStatus = styled.span<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? "#dc2626" : "#6b7280")};
  font-size: 12px;
  white-space: nowrap;
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
`;

const TimeDivider = styled.div`
  display: flex;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
  margin: 8px 0 10px;
`;

const Row = styled.div<{ $self: boolean }>`
  display: flex;
  justify-content: ${(p) => (p.$self ? "flex-end" : "flex-start")};
  margin-bottom: 8px;
`;

const BubbleWrap = styled.div<{ $self: boolean }>`
  max-width: 85%;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  flex-direction: ${(p) => (p.$self ? "row-reverse" : "row")};
`;

const Avatar = styled.div<{ $self: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: ${(p) => (p.$self ? "#ffffff" : "#4b5563")};
  background: ${(p) => (p.$self ? "#3b82f6" : "#e5e7eb")};
  flex: 0 0 32px;
`;

const Body = styled.div`
  min-width: 0;
`;

const Name = styled.div<{ $self: boolean }>`
  font-size: 12px;
  color: #9ca3af;
  margin-bottom: 2px;
  text-align: ${(p) => (p.$self ? "right" : "left")};
`;

const Bubble = styled.div<{ $self: boolean }>`
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  border-radius: 10px;
  padding: 8px 10px;
  background: ${(p) => (p.$self ? "#2563eb" : "#f3f4f6")};
  color: ${(p) => (p.$self ? "#ffffff" : "#111827")};
`;

const InputBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  padding: 8px;
`;

const WebImInputWrap = styled.div`
  .qm-tool-btn,
  .qm-send-btn {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  .qm-send-btn {
    width: 40px;
    min-width: 40px;
  }

  .qm-chat-input {
    height: 34px;
    min-height: 34px;
    font-size: 13px;
    line-height: 1.2;
    padding-left: 10px;
    padding-right: 10px;
  }

  .qm-chat-input::placeholder {
    font-size: 13px;
  }

  .qm-tool-btn .iconify,
  .qm-send-btn .iconify {
    font-size: 18px;
    width: 18px;
    height: 18px;
    flex: 0 0 18px;
  }
`;

const messageKey = (m: Message) =>
  `${m.id || ""}-${m.user || ""}-${m.content || ""}-${m.timestamp || ""}`;

const mergeUniqueMessages = (base: Message[], incoming: Message[]) => {
  const map = new Map<string, Message>();
  base.forEach((m) => map.set(messageKey(m), m));
  incoming.forEach((m) => map.set(messageKey(m), m));
  return Array.from(map.values());
};

const initials = (name?: string) => {
  const n = (name || "").trim();
  if (!n) return "?";
  return n.slice(0, 1).toUpperCase();
};

const toTimeString = (value?: number | string) => {
  if (!value) return new Date().toLocaleTimeString();
  const ts = typeof value === "string" ? Number(value) : value;
  const ms = ts < 1e12 ? ts * 1000 : ts;
  return new Date(ms).toLocaleTimeString();
};
type UploadKind = "image" | "video" | "file";

const ChatComponent: React.FC<ChatComponentType> = ({ messageList = [] }) => {
  const { conference: conf } = useContext(MeetingContext);

  const [inputValue, setInputValue] = useState("");
  const [localMessages, setLocalMessages] = useState<Message[]>(messageList);
  const [chatBlockedReason, setChatBlockedReason] = useState("");
  const [imReady, setImReady] = useState(false);
  const [imStatusText, setImStatusText] = useState("IM连接中...");
  const bottomRef = useRef<HTMLDivElement>(null);
  const imRef = useRef<WebImUi | null>(null);

  const currentName = useMemo(() => {
    const selfPeer = conf?.attendList?.find((p) => p.peerId === `${conf.roomId}_${conf.userId}`);
    if (selfPeer?.peerName) return selfPeer.peerName;
    return "我";
  }, [conf]);

  useEffect(() => {
    setLocalMessages((prev) => mergeUniqueMessages(prev, messageList));
  }, [messageList]);

  useEffect(() => {
    const loginInfo = storage.get(LOGININFOSTORE) || {};
    const loginToken = window.localStorage.getItem(LOGINTOKEN) || "";
    const hasLogin = !!String(loginToken).trim();
    const imId = Number(loginInfo.imId || 0);
    const imToken = String(loginInfo.imToken || "");
    const groupTo = Number(conf?.conferenceNo || 0);

    if (!hasLogin) {
      setImReady(false);
      setChatBlockedReason("用户未登陆，无法聊天");
      setImStatusText("未登录");
      return;
    }

    if (!imId || !imToken || !groupTo) {
      setImReady(false);
      setChatBlockedReason("登录信息缺少IM凭据，无法聊天");
      setImStatusText("IM凭据缺失");
      return;
    }

    setChatBlockedReason("");
    setImStatusText("IM连接中...");

    const wsProtocol = window.location.protocol === "https:" ? "wss://" : "ws://";
    const currentHost = window.location.hostname;
    const currentPort =
      Number(window.location.port) || (window.location.protocol === "https:" ? 443 : 80);
    // IM地址与当前视频会议地址保持一致（同域名同端口）
    const proxyServer = `${wsProtocol}${window.location.host}/imwebsocket/`;

    const im = new WebImUi({
      id: `qm_embed_${conf.conferenceNo}`,
      title: "多人聊天",
      imId,
      imToken,
      name: currentName,
      type: "group",
      theme: "light",
      to: groupTo,
      host: currentHost,
      port: currentPort,
      protocol: wsProtocol,
      proxyServer,
      showOpen: false,
    });

    imRef.current = im;

    if (im.Section) im.Section.style.display = "none";
    if (im.Dialog?.open) im.Dialog.close();

    const handleReady = () => {
      setImReady(true);
      setImStatusText("IM已连接");
    };

    const handleNotReady = () => {
      setImReady(false);
      setImStatusText("IM连接中断");
    };

    const handleError = () => {
      setImReady(false);
      setImStatusText("IM连接失败");
    };

    const handleMessage = (evt: any) => {
      const data = evt?.data || [];
      const incoming: Message[] = data
        .map((m: any) => {
          const payload = m?.payload || {};
          const text = String(payload?.text || "").trim();
          if (!text) return null;
          const flow = String(m?.flow || "");
          const user = flow === "out" ? "我" : String(payload?.name || m?.from || "匿名");
          return {
            id: String(m?.ID || `${Date.now()}_${Math.random()}`),
            user,
            content: text,
            timestamp: toTimeString(m?.time || m?.timeMs),
          } as Message;
        })
        .filter(Boolean);

      if (incoming.length) {
        setLocalMessages((prev) => mergeUniqueMessages(prev, incoming));
      }
    };

    im.service.on("sdkStateReady" as any, handleReady);
    im.service.on("sdkStateNotReady" as any, handleNotReady);
    im.service.on("error" as any, handleError);
    im.service.on("onMessageReceived" as any, handleMessage);

    return () => {
      try {
        im.service.off("sdkStateReady" as any, handleReady);
        im.service.off("sdkStateNotReady" as any, handleNotReady);
        im.service.off("error" as any, handleError);
        im.service.off("onMessageReceived" as any, handleMessage);
      } catch (e) {
        // ignore
      }
      im.destroy();
      imRef.current = null;
    };
  }, [conf?.conferenceNo, currentName]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [localMessages]);

  const getHashUsername = () => {
    const hash = window.location.hash;
    const queryString = hash.split("?")[1] || "";
    const urlParams = new URLSearchParams(queryString);
    return urlParams.get("username") || currentName || "我";
  };

  const sendMessage = async () => {
    const text = inputValue.trim();
    if (!text) return;
    if (chatBlockedReason) return;
    if (!imReady || !imRef.current) return;

    const username = getHashUsername();
    const localEcho: Message = {
      id: `${Date.now()}_${Math.random()}`,
      user: "我",
      content: text,
      timestamp: new Date().toLocaleTimeString(),
    };
    try {
      const imMsg = imRef.current.service.createTextMessage({
        to: String(conf.conferenceNo),
        conversationType: "GROUP" as any,
        payload: { text, name: username },
      } as any);
      await imRef.current.service.sendMessage(imMsg);
      // 先本地回显，避免等待IM回推期间“发送后看不到消息”
      setLocalMessages((prev) => mergeUniqueMessages(prev, [localEcho]));
    } catch (e) {
      setImStatusText("IM发送失败，请稍后重试");
      return;
    }

    setInputValue("");
  };

  const sendUploadMessage = async (kind: UploadKind) => {
    if (chatBlockedReason) return;
    if (!imReady || !imRef.current) return;
    if (!conf?.conferenceNo) return;

    const picker = document.createElement("input");
    picker.type = "file";
    picker.accept = kind === "file" ? "*/*" : `${kind}/*`;
    picker.onchange = async (evt: Event) => {
      const file = (evt.target as HTMLInputElement)?.files?.[0];
      if (!file || !imRef.current) return;

      try {
        const formData = new FormData();
        formData.append("file", file);

        const uploadResp = await fetch("/upload/images", {
          method: "POST",
          headers: {
            "restrict-access": "true",
            Accept: "*/*",
          },
          body: formData,
        });
        const uploadJson = await uploadResp.json();
        const uploadData = uploadJson?.data ? uploadJson.data : uploadJson;
        const fileUrl = uploadData?.src_url || uploadData?.url;
        if (!fileUrl) throw new Error("upload url missing");

        const username = getHashUsername();
        const imService: any = imRef.current.service as any;
        let msg: any = null;

        if (kind === "image") {
          msg = imService.createImageMessage({
            to: String(conf.conferenceNo),
            conversationType: "GROUP",
            payload: {
              name: username,
              image: fileUrl,
              file: "<file>",
              image2: {
                url: fileUrl,
                width: uploadData?.width || 0,
                height: uploadData?.height || 0,
                size: uploadData?.filesize || file.size,
                name: uploadData?.filename || file.name,
              },
            },
          });
        } else if (kind === "video") {
          msg = imService.createVideoMessage({
            to: String(conf.conferenceNo),
            conversationType: "GROUP",
            payload: {
              name: username,
              filename: file.name,
              video: {
                size: uploadData?.filesize || file.size,
                name: file.name,
                url: fileUrl,
              },
            },
          });
        } else {
          msg = imService.createFileMessage({
            to: String(conf.conferenceNo),
            conversationType: "GROUP",
            payload: {
              name: username,
              filename: file.name,
              file: {
                size: uploadData?.filesize || file.size,
                name: file.name,
                url: fileUrl,
              },
            },
          });
        }

        if (msg) {
          await imService.sendMessage(msg);
        }
      } catch (e) {
        setImStatusText("附件发送失败，请稍后重试");
      }
    };
    picker.click();
  };

  return (
    <Container>
      <HeaderBar>
        {chatBlockedReason ? (
          <HeaderAlert>{chatBlockedReason}</HeaderAlert>
        ) : (
          <HeaderStatus $error={!imReady}>{imStatusText}</HeaderStatus>
        )}
      </HeaderBar>
      <MessageList>
        {localMessages.map((m, idx) => {
          const self =
            m.user === "我" ||
            (m.user || "").trim() === (currentName || "").trim();
          const userName = m.user || (self ? "我" : "匿名");
          const time = m.timestamp || "";
          return (
            <React.Fragment key={`${messageKey(m)}-${idx}`}>
              {time && <TimeDivider>{time}</TimeDivider>}
              <Row $self={self}>
                <BubbleWrap $self={self}>
                  <Avatar $self={self}>{initials(userName)}</Avatar>
                  <Body>
                    <Name $self={self}>{userName}</Name>
                    <Bubble $self={self}>{m.content}</Bubble>
                  </Body>
                </BubbleWrap>
              </Row>
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </MessageList>
      <InputBar>
        <WebImInputWrap className="web-im-ui">
          <div className="flex flex-col justify-between items-center gap-1.5">
            <div className="flex w-full gap-1.5">
              <button
                className="btn btn-ghost btn-sm btn-circle qm-tool-btn"
                disabled={!!chatBlockedReason || !imReady}
                onClick={() => sendUploadMessage("image")}
                title="发送图片"
              >
                <span className="iconify mynaui--image"></span>
              </button>
              <button
                className="btn btn-ghost btn-sm btn-circle qm-tool-btn"
                disabled={!!chatBlockedReason || !imReady}
                onClick={() => sendUploadMessage("video")}
                title="发送视频"
              >
                <span className="iconify mynaui--video"></span>
              </button>
              <button
                className="btn btn-ghost btn-sm btn-circle qm-tool-btn"
                disabled={!!chatBlockedReason || !imReady}
                onClick={() => sendUploadMessage("file")}
                title="发送文件"
              >
                <span className="iconify mynaui--file-text"></span>
              </button>
            </div>
            <div className="w-full flex gap-1.5">
              <label htmlFor="chat-input" className="m-0 p-0 sr-only">
                Chat Input
              </label>
              <input
                id="chat-input"
                name="chat-input"
                type="text"
                className="input qm-chat-input flex-1 font-normal"
                disabled={!!chatBlockedReason || !imReady}
                value={inputValue}
                placeholder={chatBlockedReason || (imReady ? "请输入内容..." : "IM连接中，请稍候...")}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />
              <button
                className="btn p-2 qm-send-btn"
                disabled={!!chatBlockedReason || !imReady}
                onClick={sendMessage}
                title="发送"
              >
                <span className="iconify mynaui--send-solid"></span>
              </button>
            </div>
          </div>
        </WebImInputWrap>
      </InputBar>
    </Container>
  );
};

export default ChatComponent;
